import merge from 'lodash.merge'
import get from 'lodash.get'
import { currencyFormatHideZero } from '@/modules/utils'

//The order format of a purchase order line belongs to the line, not to the item variation:
//switching unit <-> case must only update the purchase order. The total amount ordered is kept,
//so 2 cases of 6 become 12 units and the other way around.
const getSaveOrderFormat = updatePurchaseOrder => (inputValue, id, type, data) => {
  let format = (inputValue && inputValue.id) || inputValue,
    value = merge({}, get(data, 'data.purchase_order_info')),
    order = value.order || {},
    caseSize = order.case_size || get(data, 'data.case_size') || 1,
    units = order.format === 'case' ? (order.quantity || 0) * (order.case_size || 1) : order.quantity || 0

  if (!value.id || (format !== 'case' && format !== 'unit') || format === order.format) {
    return
  }

  value.order = format === 'case' ? { type: 'case', format: 'case', case_size: caseSize, quantity: units / caseSize } : { type: 'unit', format: 'unit', quantity: units }

  updatePurchaseOrder({ type, value }, true)
}

const getGridColumns = ({ updatePurchaseOrder, translations, orderFormats, itemPricesById, openAdjustmentsEditor }) => ({
  dropdownMenu: {
    forceOverrideValuegetter: true,
    valueGetter: params => ({
      group: params.node.group,
      items: [
        {
          onClick: item => {
            updatePurchaseOrder({ type: 'item_delete', value: item.item_distributor_id }, true)
          },
          getDisabled: item => {
            if (item && item.purchase_order_info && !item.purchase_order_info.editable) {
              return true
            }
            return false
          },
          label: translations.txtGenericRemove,
          value: params.data
        }
      ]
    })
  },
  image: {
    hide: false
  },
  title: {
    hide: false,
    wiskBottomAggregation: translations.txtGenericTotal + ':'
  },
  category: {
    hide: false
  },
  distributorCode: {
    hide: false
  },
  stock: {
    hide: false
  },
  perpetual: {
    hide: true
  },
  par: {
    hide: true
  },
  pendingPurchaseOrders: { hide: true },
  parSuggested: { hide: true },
  parSuggestedSales: { hide: true },
  suggestedOrderWisk: { hide: true, pinned: null },
  suggestedOrderWiskSales: { hide: true, pinned: null },
  suggestedOrderRealTimeStock: { hide: true, pinned: null },
  suggestedOrder: { hide: true, pinned: null },
  costUnitPurchaseOrder: {
    colId: 'costUnitPurchaseOrder',
    hide: false,
    sortOrder: 1750,
    headerName: translations.columnCostPerUnit,
    cellRenderer: 'CellNumber',
    minWidth: 100,
    maxWidth: 120,
    cellClass: ['text-center'],
    cellRendererParams: {
      save: (inputValue, id, type, data) => {
        let value = merge({}, data.data.purchase_order_info)
        value.price_per_unit = inputValue
        value.price = { type: 'unit', value: inputValue }
        updatePurchaseOrder({ type, value }, true)
      },
      type: 'item_update',
      decimals: 2,
      prefix: window.WiskGlobals.currency
    },
    valueGetter: params => ({
      id: params.data && params.data.purchase_order_info && params.data.purchase_order_info.id,
      price: get(itemPricesById, `${get(params, 'data.item_id')}`),
      title: params.data && params.data.title,
      input_value: get(params, 'data.purchase_order_info.price_per_unit'),
      data: params.data,
      readonly: !get(params, 'data.purchase_order_info.editable')
    })
  },
  orderItemQuantity: {
    colId: 'orderItemQuantity',
    hide: false,
    sortOrder: 1750,
    headerName: translations.txtGenericQuantityToOrder,
    cellRenderer: 'CellNumber',
    minWidth: 100,
    maxWidth: 120,
    cellClass: ['text-center'],
    cellRendererParams: {
      decimals: 2,
      save: (inputValue, id, type, data) => {
        let value = merge({}, data.data.purchase_order_info)
        value.order.quantity = inputValue
        updatePurchaseOrder({ type, value }, true)
      },
      type: 'item_update'
    },
    valueGetter: params => ({
      id: get(params.data, 'purchase_order_info.id'),
      price: get(itemPricesById, `${get(params, 'data.item_id')}`),
      title: params.data && params.data.title,
      input_value: get(params, 'data.purchase_order_info.order.quantity'),
      data: params.data,
      readonly: !get(params.data, 'purchase_order_info.editable')
    })
  },
  orderFormat: {
    colId: 'orderFormat',
    cellRendererParams: {
      save: getSaveOrderFormat(updatePurchaseOrder),
      //this column shares its colId with the items grid column, which saves through
      //`updateValue: saveItemVariant`. That helper posts to `item-distributors/id/<id>` using the
      //id returned by the valueGetter below - here that is the purchase order line id (the item
      //id), so the API answered "Unable to find item variation <item_id>" and nothing was saved.
      updateValue: null,
      type: 'item_update',
      getItems: () => orderFormats,
      multiselectWidth: 230,
      required: true
    },
    cellEditorParams: {
      save: getSaveOrderFormat(updatePurchaseOrder),
      updateValue: null,
      type: 'item_update',
      getItems: () => orderFormats,
      multiselectWidth: 230,
      required: true
    },
    valueSetter: () => false,
    valueGetter: p => ({
      id: p.data && p.data.purchase_order_info && p.data.purchase_order_info.id,
      input_value: { id: get(p, 'data.purchase_order_info.order.format'), title: get(p, 'data.purchase_order_info.order.format') },
      readonly: p.data && (p.data.case_size === 1 || !p.data.purchase_order_info?.editable),
      data: p.data
    })
  },
  caseSize: {
    hide: false,
    sortOrder: 2300
  },
  adjustmentsAmount: {
    colId: 'adjustmentsAmount',
    sortOrder: 2350,
    headerName: translations.txtAdjustmentsAmount,
    cellRenderer: 'CellText',
    cellClass: ['text-end', 'currency'],
    minWidth: 100,
    cellRendererParams: {
      readonly: true,
      translate: translations.translate,
      infoComponentType: 'extraButtons',
      extraButtons: openAdjustmentsEditor
        ? [
            {
              id: 1,
              action: openAdjustmentsEditor,
              icon: 'wisk-edit',
              title: translations.txtAdjustmentAddAdjustment
            }
          ]
        : []
    },
    valueGetter: params => {
      if (!params.data) return {}
      const info = get(params, 'data.purchase_order_info', {})
      return {
        id: info.id,
        input_value: currencyFormatHideZero((info.adjustments || []).reduce((sum, a) => sum + (a.computed_total || 0), 0)),
        data: params.data
      }
    }
  },
  lineTotal: {
    colId: 'lineTotal',
    sortOrder: 2400,
    pinned: 'right',
    cellClass: ['text-end'],
    minWidth: 110,
    headerName: translations.txtGenericTotal,
    cellRenderer: 'CellNumber',
    cellRendererParams: { decimals: 2, prefix: window.WiskGlobals.currency, readonly: true },
    wiskGetBottomAggregationValue: rowData => {
      const info = get(rowData, 'purchase_order_info', {})
      return info.effective_total !== null && info.effective_total !== undefined ? info.effective_total : info.total || 0
    },
    valueGetter: params => {
      if (!params.data) return {}
      const info = get(params, 'data.purchase_order_info', {})
      return {
        id: info.id,
        input_value: info.effective_total !== null && info.effective_total !== undefined ? info.effective_total : info.total || 0,
        data: params.data
      }
    }
  },
  externalId: {}
})

export default getGridColumns
